import {
  MutationKey,
  useMutation,
  UseMutationResult,
  UseMutationOptions,
} from '@tanstack/react-query';
import {AxiosRequestConfig} from 'axios';

import {METHOD_WITH_REQUEST_BODY} from 'src/js/common/constants';
import {ApiClient} from 'src/js/common/utils/api_client';
import {QueryClientContext} from 'src/js/common/utils/query_client';

type UseApiMutationOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
> = {
  axiosConfig?: AxiosRequestConfig<TData>;
  reactMutationOptions?: UseMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  >;
};

export function useApiMutation<
  TData = unknown,
  TError = unknown,
  TMutationKey extends MutationKey = MutationKey,
  TVariables = void,
  TContext = unknown
>(
  key: TMutationKey,
  data?: TData | null,
  options?: UseApiMutationOptions<TData, TError, TVariables, TContext>,
  isAuthorized?: boolean
): UseMutationResult<TData, TError, TVariables, TContext> {
  return useMutation<TData, TError, TVariables, TContext>(
    key,
    (): Promise<TData> => {
      try {
        const isDataRequired =
          options?.axiosConfig?.method == null
            ? false
            : METHOD_WITH_REQUEST_BODY.some(
                (item) =>
                  item.trim().toUpperCase() ===
                  String(options.axiosConfig?.method).trim().toUpperCase()
              );
        const clientConfig = {
          ...options?.axiosConfig,
          url:
            options?.axiosConfig?.url ||
            (typeof key === 'string' ? key : undefined),
          ...(isDataRequired
            ? {data: options?.axiosConfig?.data || data}
            : {data: undefined}),
        };
        return ApiClient.getData(clientConfig, isAuthorized);
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      }
    },
    {context: QueryClientContext, ...options?.reactMutationOptions}
  );
}
